import type React from 'react';

import { Box, Divider, Label } from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { SEPARATE_HEATING_CHARGE } from '../../../../constants';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import HeaderToggle from '../Utilities/HeaderToggle';
import CurrencyInput from '../../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function SeparateHeatingCharge_v4({ section, setSectionValues }: Props) {
  const heatingChargeApplies = section.getFieldValue(SEPARATE_HEATING_CHARGE.FIELD_NAME.IS_ENABLED);
  const paysInAdditionToRent = section.getFieldValue(
    SEPARATE_HEATING_CHARGE.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );
  const fixedAmountValue = section.getFieldValue(SEPARATE_HEATING_CHARGE.FIELD_NAME.FIXED_AMOUNT_VALUE);

  function toggleUtility(): void {
    setSectionValues({
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(
        SEPARATE_HEATING_CHARGE.FIELD_NAME.IS_ENABLED,
      ),
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: false,
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.FIXED_AMOUNT_VALUE]: '',
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        SEPARATE_HEATING_CHARGE.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
    });
  }

  function setFixedAmountValue(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Is heat provided separately from other utilities?"
        subheader="Applies to this property"
        switchOn={heatingChargeApplies}
        onSwitchToggle={toggleUtility}
      />
      {heatingChargeApplies && (
        <>
          <Box>
            <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
            <SwitchWithTitle
              text="Tenant pays in addition to base rent"
              onClick={togglePaysInAddition}
              toggled={paysInAdditionToRent}
            />
          </Box>
          {paysInAdditionToRent && (
            <Box css={{ marginTop: 'layout.tight' }}>
              <Label>What is the additional fixed fee for heat?</Label>
              <CurrencyInput
                aria-label="fixed amount"
                data-test="fixedAmountValue"
                value={fixedAmountValue || ''}
                onBlur={setFixedAmountValue}
                onChange={setFixedAmountValue}
                endAdornment="per month"
              />
            </Box>
          )}
        </>
      )}
    </>
  );
}

SeparateHeatingCharge_v4.description = 'Fixed fee value instead of asking landord to bill tenant actual cost';

export default SeparateHeatingCharge_v4;
